import { useCallback, useEffect, useState } from "react";
import { X, Plus, Minus, Trash2, Gift, Truck, Check } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useShop } from "../contexts/ShopContext";
import { formatPrice } from "../lib/format";
import api from "../lib/api";
import { toast } from "sonner";

export default function CartDrawer() {
  const { cart, cartOpen, setCartOpen, updateQty, removeFromCart, subtotal, discount, coupon, setCoupon, pendingCoupon, dismissPendingCoupon } = useShop();
  const nav = useNavigate();
  const [eligible, setEligible] = useState([]);
  const [picking, setPicking] = useState(false);

  const loadEligible = useCallback(async () => {
    if (cart.length === 0) { setEligible([]); return; }
    try {
      const items = cart.map(i => ({ product_id: i.product_id, price: i.price, quantity: i.quantity }));
      const { data } = await api.post("/coupons/auto-apply", { code: "", subtotal, items });
      setEligible(data.eligible || []);
    } catch { /* silent — auto-apply is best-effort */ }
  }, [cart, subtotal]);

  useEffect(() => { if (cartOpen) loadEligible(); }, [cartOpen, loadEligible]);

  const applyAuto = (c) => {
    setCoupon(c);
    setPicking(false);
    toast.success(`Auto-applied: ${c.label || c.code}`);
  };

  if (!cartOpen) return null;

  const canShowBanner = !coupon && eligible.length > 0;
  const bannerCoupon = eligible[0];

  return (
    <div className="fixed inset-0 z-[60]">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setCartOpen(false)} />
      <aside className="absolute right-0 top-0 h-full w-full max-w-md bg-[#0C0C0C] border-l border-[rgba(244,244,240,0.06)] flex flex-col" data-testid="cart-drawer">
        <div className="h-20 px-6 flex items-center justify-between border-b border-[rgba(244,244,240,0.06)]">
          <span className="text-xs uppercase tracking-[0.25em] text-gold">Your Bag · {cart.length}</span>
          <button onClick={() => setCartOpen(false)} data-testid="close-cart"><X size={20} strokeWidth={1.5} /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-6">
          {cart.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center gap-4">
              <p className="font-serif text-2xl">Your bag is empty</p>
              <p className="text-muted text-sm">Begin curating your edit.</p>
              <button className="btn-outline mt-4" onClick={() => { setCartOpen(false); nav("/shop"); }}>Explore Collection</button>
            </div>
          ) : (
            <ul className="space-y-6">
              {cart.map((item) => (
                <li key={`${item.product_id}-${item.size}`} className="flex gap-4" data-testid={`cart-item-${item.product_id}`}>
                  <img src={item.image} alt={item.name} className="w-24 h-32 object-cover" />
                  <div className="flex-1 flex flex-col justify-between">
                    <div>
                      <h4 className="font-serif text-lg">{item.name}</h4>
                      <p className="text-xs uppercase tracking-[0.18em] text-muted mt-1">Size {item.size}</p>
                      <p className="text-sm text-gold mt-2">{formatPrice(item.price * item.quantity)}</p>
                    </div>
                    <div className="flex items-center justify-between mt-3">
                      <div className="flex items-center gap-3 border border-[rgba(244,244,240,0.1)] px-2 py-1">
                        <button onClick={() => updateQty(item.product_id, item.size, item.quantity - 1)} className="text-muted hover:text-gold"><Minus size={14} /></button>
                        <span className="text-sm w-6 text-center">{item.quantity}</span>
                        <button onClick={() => updateQty(item.product_id, item.size, item.quantity + 1)} className="text-muted hover:text-gold"><Plus size={14} /></button>
                      </div>
                      <button onClick={() => removeFromCart(item.product_id, item.size)} className="text-muted hover:text-gold" data-testid={`remove-item-${item.product_id}`}>
                        <Trash2 size={16} strokeWidth={1.5} />
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {cart.length > 0 && (
          <div className="px-6 py-6 border-t border-[rgba(244,244,240,0.06)] space-y-4">
            {pendingCoupon && !coupon && (
              <div
                className="flex items-center gap-2 bg-[rgba(74,222,128,0.08)] border border-[rgba(74,222,128,0.3)] text-[#86efac] px-3 py-2"
                data-testid="pending-coupon-pill"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-[#86efac] animate-pulse" aria-hidden="true" />
                <div className="flex-1 text-[11px] leading-snug">
                  <span className="uppercase tracking-[0.18em] font-medium">{pendingCoupon.code}</span>
                  <span className="ml-1.5 opacity-80">· Keep shopping — {pendingCoupon.reason.replace(/^Add /, "add ")}</span>
                </div>
                <button
                  type="button"
                  onClick={dismissPendingCoupon}
                  className="opacity-60 hover:opacity-100 -m-1 p-1"
                  aria-label="Dismiss"
                  data-testid="dismiss-pending-coupon"
                >
                  <X size={12} strokeWidth={1.5} />
                </button>
              </div>
            )}
            {canShowBanner && (
              eligible.length === 1 ? (
                <button
                  type="button"
                  onClick={() => applyAuto(bannerCoupon)}
                  className="w-full bg-gold/10 border border-gold/30 text-gold px-4 py-3 flex items-center gap-3 hover:bg-gold/15 transition-colors"
                  data-testid="auto-apply-banner"
                >
                  <Gift size={14} strokeWidth={1.5} />
                  <div className="flex-1 text-left">
                    <p className="text-[10px] uppercase tracking-[0.22em]">Auto-apply · {bannerCoupon.label || bannerCoupon.code}</p>
                    <p className="text-xs text-muted mt-0.5">You save {formatPrice(bannerCoupon.discount_amount)}</p>
                  </div>
                  <span className="text-[10px] uppercase tracking-[0.18em]">Apply →</span>
                </button>
              ) : (
                <div className="bg-gold/10 border border-gold/30 px-4 py-3 space-y-2" data-testid="auto-apply-picker">
                  <p className="text-[10px] uppercase tracking-[0.22em] text-gold flex items-center gap-2"><Gift size={12} strokeWidth={1.5} /> {eligible.length} offers available — pick one</p>
                  {(picking ? eligible : eligible.slice(0, 3)).map(c => (
                    <button
                      key={c.coupon_id}
                      type="button"
                      onClick={() => applyAuto(c)}
                      className="w-full flex items-center justify-between text-xs bg-[#0C0C0C] px-3 py-2 hover:bg-[#141414] transition-colors"
                      data-testid={`auto-apply-pick-${c.code}`}
                    >
                      <span className="text-gold">{c.label || c.code}</span>
                      <span className="text-muted">Save {formatPrice(c.discount_amount)}</span>
                    </button>
                  ))}
                  {!picking && eligible.length > 3 && (
                    <button type="button" onClick={() => setPicking(true)} className="text-[10px] text-muted hover:text-gold">See {eligible.length - 3} more offers</button>
                  )}
                </div>
              )
            )}
            {coupon && (
              <div className="bg-gold/10 border border-gold/30 px-4 py-2 flex items-center justify-between" data-testid="active-coupon-pill">
                <p className="text-[10px] uppercase tracking-[0.22em] text-gold">Applied · {coupon.label || coupon.code}</p>
                <button type="button" onClick={() => setCoupon(null)} className="text-muted hover:text-gold text-[10px] uppercase tracking-[0.18em]" data-testid="remove-coupon-btn">Remove</button>
              </div>
            )}
            <div className="flex justify-between text-sm">
              <span className="text-muted uppercase tracking-[0.2em] text-xs">Subtotal</span>
              <span className="text-gold">{formatPrice(subtotal)}</span>
            </div>
            <ShippingProgress netTotal={Math.max(0, subtotal - (discount || 0))} />
            <button className="btn-gold w-full" onClick={() => { setCartOpen(false); nav("/checkout"); }} data-testid="checkout-btn">
              Checkout
            </button>
          </div>
        )}
      </aside>
    </div>
  );
}

const FREE_SHIPPING_THRESHOLD = 1000;

function ShippingProgress({ netTotal }) {
  const unlocked = netTotal >= FREE_SHIPPING_THRESHOLD;
  const pct = Math.min(100, Math.round((netTotal / FREE_SHIPPING_THRESHOLD) * 100));
  const remaining = Math.max(0, FREE_SHIPPING_THRESHOLD - netTotal);

  return (
    <div className="space-y-2" data-testid="shipping-progress">
      <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.22em]">
        <span className="flex items-center gap-2 text-gold">
          {unlocked ? <Check size={12} strokeWidth={2} /> : <Truck size={12} strokeWidth={1.5} />}
          {unlocked ? "Complimentary shipping unlocked" : "Complimentary shipping"}
        </span>
        <span className="text-muted" data-testid="shipping-progress-pct">{pct}%</span>
      </div>
      <div className="w-full h-1 bg-[rgba(244,244,240,0.06)] overflow-hidden">
        <div
          className={`h-full transition-all duration-500 ${unlocked ? "bg-[#C8A97E]" : "bg-[#C8A97E]/70"}`}
          style={{ width: `${pct}%` }}
          data-testid="shipping-progress-bar"
        />
      </div>
      <p className="text-[11px] text-muted leading-relaxed" data-testid="shipping-progress-msg">
        {unlocked ? (
          <>You've qualified for complimentary shipping. Taxes calculated at checkout.</>
        ) : (
          <>Add <span className="text-gold">{formatPrice(remaining)}</span> more to unlock complimentary shipping.</>
        )}
      </p>
    </div>
  );
}
